import React, { useEffect, useRef, useState } from "react";
import { X, Send, User, Loader2, MessageSquare } from "lucide-react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  PlanCardCustomer, DisclosureCardCustomer, TermsCardCustomer,
  OtpCardCustomer, PaymentCardCustomer, CompleteCardCustomer,
  CustomerDataCard,
} from "./ChatCustomerCards";

/**
 * ChatWidget — public floating chat widget for Velcor.
 *
 * Lives on all public pages (Home, /enroll, /plans, city pages). Bottom-right
 * floating bubble → click expands to a 380px panel:
 *   1. Pre-chat form (first+last name required, email + objective optional,
 *      language toggle es/en).
 *   2. Conversation with AI (GPT-5.2 via backend) + business-hours badge.
 *   3. When AI escalates, the customer sees "Conectándote con un agente…"
 *      and a human can reply from /admin/inbox.
 *
 * Persistence: session_id stored in sessionStorage (per-tab) so a refresh
 * preserves the conversation without leaking to other tabs / users.
 */

const STORAGE_KEY = "velcor_chat_session";
const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "") + "/api";

// Quick-reply chips surfaced right after the greeting. Tapping a chip sends
// the canned text as a user message — same code path as typing manually.
// Phrasing intentionally matches our intent detectors on the backend:
// • "Quiero contratar" → _detect_order_intent → wizard
// • "Soporte" → _detect_support_keyword → escalate to human
const QUICK_REPLIES_ES = [
  { id: "contratar", label: "Quiero contratar un plan", text: "Quiero contratar un plan" },
  { id: "cuenta", label: "Estado de mi cuenta", text: "Quiero ver el estado de mi cuenta" },
  { id: "facturacion", label: "Facturación y pagos", text: "Tengo una pregunta sobre facturación y pagos" },
  { id: "soporte", label: "Hablar con un agente", text: "Soporte" },
];
const QUICK_REPLIES_EN = [
  { id: "contratar", label: "Sign up for a plan", text: "I want to sign up for a plan" },
  { id: "cuenta", label: "My account status", text: "I want to see my account status" },
  { id: "facturacion", label: "Billing and payments", text: "I have a question about billing and payments" },
  { id: "soporte", label: "Talk to an agent", text: "Support" },
];

const COPY = {
  es: {
    chat_with_us: "Habla con nosotros",
    pre_title: "¡Hola! ¿En qué te ayudamos?",
    pre_intro: "Para ayudarte mejor, completa la siguiente información antes de comenzar tu chat.",
    pre_required: "Requerido *",
    full_name: "¿Cuál es tu nombre completo?",
    email: "Email",
    address: "Dirección de servicio (incluye ZIP)",
    address_hint: "ej. 1500 Louisiana St, Houston, TX 77002 — o tu ESIID de 17 dígitos",
    start: "Iniciar chat",
    privacy_policy: "Política de Privacidad",
    online: "Agentes en línea",
    offline: "Fuera de horario · AI te ayuda",
    typing: "Velcor escribiendo…",
    placeholder: "Escribe tu mensaje…",
    escalated: "Esperando un agente de soporte…",
    powered: "Asistido por AI · respuestas no son contractuales",
    closed_chat: "Cerrar chat",
    lang_label: "Idioma",
    close_confirm_title: "¿Cómo quieres cerrar?",
    close_minimize: "Minimizar",
    close_minimize_sub: "Conservo la conversación abierta",
    close_finish: "Finalizar chat",
    close_finish_sub: "Borra la sesión y empieza desde cero",
    close_cancel: "Cancelar",
  },
  en: {
    chat_with_us: "Chat with us",
    pre_title: "Hi! How can we help?",
    pre_intro: "To help us serve you better, please provide some information before we begin your chat.",
    pre_required: "Required *",
    full_name: "What is your full name?",
    email: "Email",
    address: "Service address (including ZIP)",
    address_hint: "ex. 1500 Louisiana St, Houston, TX 77002 — or your 17-digit ESIID",
    start: "Begin Chat",
    privacy_policy: "Privacy Policy",
    online: "Agents online",
    offline: "After hours · AI is helping",
    typing: "Velcor is typing…",
    placeholder: "Type your message…",
    escalated: "Waiting for a support agent…",
    powered: "AI-assisted · responses are not contractual",
    closed_chat: "Close chat",
    lang_label: "Language",
    close_confirm_title: "How do you want to close?",
    close_minimize: "Minimize",
    close_minimize_sub: "Keep the conversation open",
    close_finish: "End chat",
    close_finish_sub: "Delete the session and start over",
    close_cancel: "Cancel",
  },
};

export default function ChatWidget() {
  const { i18n } = useTranslation();
  // Default chat language mirrors the site's current language so a customer
  // browsing the English version of the site starts the chat in English.
  // They can still flip with the Español/English buttons in the prechat.
  const siteLang = i18n.language?.startsWith("en") ? "en" : "es";
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("prechat"); // prechat | chat
  const [lang, setLang] = useState(siteLang);
  const [businessHours, setBusinessHours] = useState(null);
  // Pre-chat fields — Direct Energy-style: single full name field, email
  // (used to detect existing customer accounts), and address (validated
  // server-side). All three are required to keep the wizard data-rich
  // from the very first message.
  const [form, setForm] = useState({ full_name: "", email: "", address: "" });
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  // Chat state
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const [chatClosed, setChatClosed] = useState(false);
  const [closeMenu, setCloseMenu] = useState(false);
  // Agent presence — surfaced by the transcript endpoint. `agentTyping`
  // drives the "Velcor escribiendo…" bubble; `agentName` lets us label it
  // with the actual person ("Carlos escribiendo…") once a chat is claimed.
  const [agentTyping, setAgentTyping] = useState(false);
  const [agentName, setAgentName] = useState("");
  // Whether a human agent has joined the chat — flips true as soon as
  // we see a message with `sender_role: "agent"` OR the session has an
  // `assigned_to`. Used to auto-dismiss the "Esperando agente" pill so
  // the conversation feels like a normal WhatsApp once Velcor accepts.
  const [agentJoined, setAgentJoined] = useState(false);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const typingHbRef = useRef(0);

  const t = COPY[lang];

  // If the user toggles the SITE language and hasn't started a chat yet,
  // mirror the change in the prechat. Once a session is started we lock
  // the language (server side it's persisted on the session doc).
  useEffect(() => {
    if (view === "prechat" && !sessionId) {
      setLang(i18n.language?.startsWith("en") ? "en" : "es");
    }
  }, [i18n.language, view, sessionId]);

  // Restore session from sessionStorage
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved.session_id) {
          setSessionId(saved.session_id);
          setLang(saved.language || "es");
          setView("chat");
          loadTranscript(saved.session_id);
        }
      }
    } catch { /* ignore */ }
    // Fetch business hours
    axios.get(`${API}/chat/business-hours`).then((r) => setBusinessHours(r.data?.business_hours))
      .catch(() => setBusinessHours(null));
    // Listen for global "open chat" events fired by CTAs on the site
    const openHandler = () => setOpen(true);
    window.addEventListener("velcor:open-chat", openHandler);
    return () => window.removeEventListener("velcor:open-chat", openHandler);
  }, []);

  // Ref mirror of `messages` so async helpers (polling, loadTranscript)
  // always see the latest list — closures captured at registration time
  // would otherwise become stale and produce duplicate inserts.
  const messagesRef = useRef([]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current && view === "chat") {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, view]);

  // Polling for human agent replies (every 5s while in chat view)
  useEffect(() => {
    if (view !== "chat" || !sessionId) return;
    pollRef.current = setInterval(() => loadTranscript(sessionId, true), 5000);
    return () => clearInterval(pollRef.current);
  }, [view, sessionId]);

  // Presence heartbeat — tells the backend the customer is still here.
  // Every 20s + on visibilitychange. When the customer closes the tab or
  // navigates away, navigator.sendBeacon flushes a `leave` event so the
  // agent's inbox immediately shows "Cliente cerró el chat" instead of
  // them waiting on a phantom customer.
  useEffect(() => {
    if (view !== "chat" || !sessionId) return undefined;
    const ping = () => {
      axios.post(`${API}/chat/${sessionId}/ping`).catch(() => {});
    };
    ping(); // fire one immediately
    const id = setInterval(ping, 20000);
    const onUnload = () => {
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon(`${API}/chat/${sessionId}/leave`);
        }
      } catch (_e) { /* best-effort */ }
    };
    const onVis = () => {
      if (document.visibilityState === "visible") ping();
    };
    window.addEventListener("beforeunload", onUnload);
    window.addEventListener("pagehide", onUnload);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      window.removeEventListener("beforeunload", onUnload);
      window.removeEventListener("pagehide", onUnload);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [view, sessionId]);

  const loadTranscript = async (sid, silent = false, explicitSince = null) => {
    try {
      const live = messagesRef.current;
      const last = live[live.length - 1];
      // explicitSince lets postMessage force a sweep starting from BEFORE
      // the optimistic AI bubble — needed when the backend inserts
      // side-effect messages (plan_cards, customer_data_card…) between
      // the user message and the AI reply.
      const sinceTs = explicitSince || (silent && last ? last.created_at : null);
      const sinceParam = sinceTs ? `?since=${encodeURIComponent(sinceTs)}` : "";
      const { data } = await axios.get(`${API}/chat/${sid}/transcript${sinceParam}`);
      if (data?.messages?.length) {
        setMessages((prev) => {
          const seenIds = new Set(prev.map((m) => m.id));
          const fresh = data.messages.filter((m) => !seenIds.has(m.id));
          // If any of the fresh messages is from a human agent, mark the
          // chat as "agent joined" so the waiting pill disappears.
          const freshAgentMsg = fresh.find((m) => m.sender_role === "agent");
          if (freshAgentMsg) {
            setAgentJoined(true);
            // "Push" the customer: subtle audio ping + window-title blink so
            // the customer notices the agent reply even if the widget is
            // minimized or the tab is unfocused.
            try {
              const Ctx = window.AudioContext || window.webkitAudioContext;
              if (Ctx) {
                const ctx = new Ctx();
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain); gain.connect(ctx.destination);
                osc.type = "sine";
                osc.frequency.setValueAtTime(880, ctx.currentTime);
                osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.18);
                gain.gain.setValueAtTime(0.14, ctx.currentTime);
                gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
                osc.start();
                osc.stop(ctx.currentTime + 0.3);
              }
            } catch { /* audio blocked */ }
            if (document.hidden) {
              const original = document.title;
              let on = false;
              const interval = setInterval(() => {
                document.title = on ? original : `💬 ${lang === "en" ? "New message" : "Nuevo mensaje"} · Velcor`;
                on = !on;
              }, 1000);
              const stop = () => { clearInterval(interval); document.title = original; document.removeEventListener("visibilitychange", stop); };
              document.addEventListener("visibilitychange", stop);
              setTimeout(stop, 20000);
            }
          }
          return [...prev, ...fresh].sort((a, b) => {
            // Keep chronological order even when side-effect messages
            // (plan_cards inserted by AI tools) arrive after an optimistic
            // AI bubble that has a LATER timestamp.
            const ta = a.created_at ? Date.parse(a.created_at) : 0;
            const tb = b.created_at ? Date.parse(b.created_at) : 0;
            return ta - tb;
          });
        });
      }
      // Agent presence — exposed by the transcript endpoint.
      if (typeof data?.agent_typing === "boolean") setAgentTyping(data.agent_typing);
      if (data?.agent_name) setAgentName(data.agent_name);
      // Once the backend tells us a real agent is assigned, treat the
      // chat as accepted even if we haven't seen a message yet (covers
      // the brief window between /claim and the first poll picking up
      // the auto-greeting).
      if (data?.assigned_to) setAgentJoined(true);
      if (data?.session_status === "escalated") setEscalated(true);
      if (data?.session_status === "resolved") {
        setChatClosed(true);
        setEscalated(false);
      }
      // If the backend archived this session (inactivity timeout, admin
      // close), wipe local state so the next interaction starts fresh.
      if (data?.session_status === "archived") {
        sessionStorage.removeItem(STORAGE_KEY);
        setTimeout(() => {
          setSessionId(null);
          setMessages([]);
          setView("prechat");
          setEscalated(false);
          setChatClosed(false);
          setForm({ full_name: "", email: "", address: "" });
        }, 5000); // give the user 5s to read the closure message
      }
      if (data?.business_hours !== undefined) setBusinessHours(data.business_hours);
    } catch { /* silent */ }
  };

  const startChat = async (e) => {
    e?.preventDefault();
    setError("");
    const fullName = (form.full_name || "").trim();
    if (!fullName) {
      setError(lang === "en" ? "Your full name is required." : "Tu nombre completo es obligatorio.");
      return;
    }
    if (!form.email.trim()) {
      setError(lang === "en" ? "Email is required." : "El email es obligatorio.");
      return;
    }
    if (!form.address.trim()) {
      setError(lang === "en"
        ? "Address is required (full street + city + ZIP) or your 17-digit ESIID."
        : "La dirección es obligatoria (calle + ciudad + ZIP) o tu ESIID de 17 dígitos.");
      return;
    }
    // Split the single full-name field into first/last for the API.
    // First token = first name, remainder = last name. If the customer
    // only typed one word we send the same value for both — the backend
    // requires both fields to be non-empty.
    const parts = fullName.split(/\s+/).filter(Boolean);
    const first_name = parts[0] || fullName;
    const last_name = parts.length > 1 ? parts.slice(1).join(" ") : first_name;
    setStarting(true);
    try {
      const payload = {
        first_name,
        last_name,
        email: form.email.trim(),
        address: form.address.trim(),
        language: lang,
      };
      const { data } = await axios.post(`${API}/chat/start`, payload);
      setSessionId(data.session_id);
      setBusinessHours(data.business_hours);
      setMessages([{
        id: data.greeting_id,
        role: "assistant",
        text: data.greeting,
        created_at: data.greeting_created_at || new Date().toISOString(),
      }]);
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ session_id: data.session_id, language: lang }));
      setView("chat");
    } catch (err) {
      const det = err?.response?.data?.detail;
      if (det && typeof det === "object" && det.message) {
        setError(det.message);
      } else if (typeof det === "string") {
        setError(det);
      } else {
        setError(lang === "en" ? "We couldn't start the chat." : "No pudimos iniciar el chat.");
      }
    } finally {
      setStarting(false);
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    await postMessage(text);
  };

  // Quick-reply chip handler — sends the canned text using the same
  // pipeline as a manually typed message.
  const sendQuickReply = async (text) => {
    if (sending) return;
    await postMessage(text);
  };

  const postMessage = async (text) => {
    setSending(true);
    // Optimistic user bubble while the round-trip is in flight. We give it
    // a temp id and REPLACE it with the canonical server id once the
    // response comes back — that way the polling loop's dedupe set
    // recognises it correctly and never renders the same message twice.
    const tempId = `tmp-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: tempId, role: "user", text, created_at: new Date().toISOString() },
    ]);
    // Mark the request start time so we can pace the AI reply naturally
    // even if the backend responds instantly.
    const requestStartedAt = Date.now();
    try {
      const { data } = await axios.post(`${API}/chat/${sessionId}/message`, { text });

      // 1) Swap the optimistic user bubble for the canonical server one,
      //    BUT keep the typing indicator on (don't show the AI reply yet).
      setMessages((prev) => {
        const noTemp = prev.filter((m) => m.id !== tempId);
        const seen = new Set(noTemp.map((m) => m.id));
        const userAddition = (data?.user_message_id && !seen.has(data.user_message_id))
          ? [{
              id: data.user_message_id,
              role: "user",
              text,
              created_at: data.user_message_created_at || new Date().toISOString(),
            }]
          : [];
        return [...noTemp, ...userAddition];
      });

      // 2) Pace the AI reveal so it feels human-typed instead of instant.
      //    ~30ms per character, clamped to [900ms, 3200ms], plus a small
      //    ±200ms jitter so the pattern isn't perfectly predictable.
      //    Subtract the actual network latency we've already burned so the
      //    visible delay is consistent regardless of backend speed.
      if (data?.reply && data?.ai_message_id) {
        const base = Math.min(3200, Math.max(900, (data.reply.length || 30) * 30));
        const jitter = (Math.random() - 0.5) * 400; // -200ms .. +200ms
        const charDelay = base + jitter;
        const elapsed = Date.now() - requestStartedAt;
        const remaining = Math.max(0, charDelay - elapsed);
        if (remaining > 0) {
          await new Promise((res) => setTimeout(res, remaining));
        }
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          if (seen.has(data.ai_message_id)) return prev;
          return [...prev, {
            id: data.ai_message_id,
            role: "assistant",
            text: data.reply,
            created_at: data.ai_message_created_at || new Date().toISOString(),
          }];
        });
        // Side-effect sweep: backend tools (plan_cards, customer_data_card,
        // etc.) may have inserted messages BETWEEN the user message and
        // the AI reply. Polling with `since=last_message` would skip them
        // because the optimistic AI bubble is now the last message.
        // Force a fetch starting from the user message's timestamp.
        if (data?.user_message_created_at) {
          loadTranscript(sessionId, true, data.user_message_created_at);
        }
      }
      if (data?.escalated) setEscalated(true);
      // Backend closed the session (e.g. support keyword after hours).
      // Hide the "esperando agente" label and disable input.
      if (data?.closed || data?.session_status === "resolved") {
        setChatClosed(true);
        setEscalated(false);
      }
    } catch (err) {
      // 409 = session archived (e.g. inactivity timeout). Wipe local state.
      if (err?.response?.status === 409) {
        sessionStorage.removeItem(STORAGE_KEY);
        setMessages((prev) => [
          ...prev.filter((m) => m.id !== tempId),
          { id: `archived-${Date.now()}`, role: "assistant",
            text: lang === "en" ? "This conversation was closed. Starting a new one…"
                                : "Esta conversación fue cerrada. Iniciando una nueva…",
            created_at: new Date().toISOString() },
        ]);
        setTimeout(() => {
          setSessionId(null);
          setMessages([]);
          setView("prechat");
          setEscalated(false);
          setChatClosed(false);
          setForm({ full_name: "", email: "", address: "" });
        }, 2500);
        return;
      }
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== tempId),
        { id: `err-${Date.now()}`, role: "assistant",
          text: lang === "en" ? "Connection error. Try again in a moment." : "Error de conexión. Intenta en un momento.",
          created_at: new Date().toISOString() },
      ]);
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setSessionId(null);
    setMessages([]);
    setView("prechat");
    setEscalated(false);
    setChatClosed(false);
    setAgentJoined(false);
    setAgentTyping(false);
    setAgentName("");
    setForm({ full_name: "", email: "", address: "" });
  };

  return (
    <>
      {/* Floating bubble — compact icon-only FAB (clean, no ring) */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed right-4 z-[9999] grid place-items-center w-12 h-12 rounded-full bg-velcor-green text-white shadow-[0_14px_32px_-10px_rgba(15,29,61,0.45)] hover:bg-velcor-green-dark hover:scale-105 transition-transform"
          style={{
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
          }}
          data-testid="chat-bubble"
          aria-label={t.chat_with_us}
          title={t.chat_with_us}
        >
          <MessageSquare className="w-6 h-6" strokeWidth={2.2} />
        </button>
      )}

      {/* Chat panel — auto-height during prechat (no empty space), fixed
          height during chat so the message list has room to scroll. */}
      {open && (
        <div className={`fixed right-4 z-[9999] w-[86vw] max-w-[320px] ${view === "chat" ? "h-[460px]" : "h-auto"} max-h-[72vh] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden`}
             style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}
             data-testid="chat-panel">
          {/* Header */}
          <header className="bg-velcor-ink text-white px-3.5 py-2.5 flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-white grid place-items-center overflow-hidden shrink-0">
              <img src="/velcor-logo.png" alt="Velcor" className="w-6 h-6 object-contain" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-black text-[13px] truncate">Velcor Assistant</div>
              <div className="text-[10.5px] opacity-80 flex items-center gap-1.5">
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${businessHours ? "bg-emerald-400" : "bg-amber-400"}`}></span>
                {businessHours ? t.online : t.offline}
              </div>
            </div>
            <button
              onClick={() => {
                // If the customer hasn't started a chat yet (still in the
                // prechat form), the X should just close the widget — no
                // need to ask "minimize vs finalize" because there's
                // nothing to discard.
                if (view === "prechat" || !sessionId) {
                  setOpen(false);
                  return;
                }
                setCloseMenu(true);
              }}
              className="opacity-70 hover:opacity-100"
              aria-label={t.closed_chat}
              data-testid="chat-close"
            >
              <X className="w-4 h-4" />
            </button>
          </header>

          {/* Close menu — overlay shown when the user taps the X. Lets them
              choose between minimizing (keep session) and finalizing (wipe).
              Asking before destroying state prevents accidental data loss. */}
          {closeMenu && (
            <div className="absolute inset-0 z-10 bg-black/45 backdrop-blur-[2px] grid place-items-center p-5"
                 onClick={() => setCloseMenu(false)}
                 data-testid="chat-close-menu">
              <div onClick={(e) => e.stopPropagation()}
                   className="w-full max-w-[300px] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100">
                  <div className="text-velcor-ink font-extrabold text-[14.5px] leading-tight">{t.close_confirm_title}</div>
                </div>
                <button
                  onClick={() => { setCloseMenu(false); setOpen(false); }}
                  className="w-full text-left px-5 py-3 hover:bg-slate-50 border-b border-slate-100"
                  data-testid="chat-close-minimize"
                >
                  <div className="text-velcor-ink font-extrabold text-[13.5px]">{t.close_minimize}</div>
                  <div className="text-slate-500 text-[11.5px] mt-0.5">{t.close_minimize_sub}</div>
                </button>
                <button
                  onClick={() => {
                    // Tell the agent the customer explicitly ended the chat
                    // (vs just minimizing) so the inbox can flag it red.
                    try {
                      if (sessionId && navigator.sendBeacon) {
                        navigator.sendBeacon(`${API}/chat/${sessionId}/leave`);
                      }
                    } catch (_e) { /* best-effort */ }
                    reset(); setCloseMenu(false); setOpen(false);
                  }}
                  className="w-full text-left px-5 py-3 hover:bg-rose-50 border-b border-slate-100"
                  data-testid="chat-close-finish"
                >
                  <div className="text-rose-700 font-extrabold text-[13.5px]">{t.close_finish}</div>
                  <div className="text-slate-500 text-[11.5px] mt-0.5">{t.close_finish_sub}</div>
                </button>
                <button
                  onClick={() => setCloseMenu(false)}
                  className="w-full text-center px-5 py-2.5 text-slate-500 font-bold text-[12.5px] hover:bg-slate-50"
                  data-testid="chat-close-cancel"
                >
                  {t.close_cancel}
                </button>
              </div>
            </div>
          )}

          {view === "prechat" ? (
            /* Pre-chat form — Direct Energy-style spacious layout. Large
               inputs, generous spacing, single full-name field, email used
               to detect existing customers, address validated server-side.
               Language is inherited from the website's global language
               state (no in-chat toggle needed). */
            <form onSubmit={startChat} className="px-4 pt-4 pb-4 flex flex-col gap-3.5 overflow-y-auto" style={{ maxHeight: "calc(72vh - 60px)" }} data-testid="chat-prechat">
              <p className="text-velcor-ink text-[13px] leading-relaxed">
                {t.pre_intro}
              </p>

              <div className="text-velcor-ink font-bold text-[12px] -mb-1">
                {t.pre_required}
              </div>

              <input
                type="text"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                placeholder={t.full_name}
                required
                autoComplete="name"
                className="w-full h-11 px-3 rounded-lg border border-slate-300 text-velcor-ink text-[13px] focus:border-velcor-green focus:outline-none focus:ring-2 focus:ring-velcor-green/20 placeholder:text-slate-400"
                data-testid="chat-full-name"
              />

              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={t.email}
                required
                autoComplete="email"
                className="w-full h-11 px-3 rounded-lg border border-slate-300 text-velcor-ink text-[13px] focus:border-velcor-green focus:outline-none focus:ring-2 focus:ring-velcor-green/20 placeholder:text-slate-400"
                data-testid="chat-email"
              />

              <div className="space-y-1">
                <input
                  type="text"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder={t.address}
                  required
                  autoComplete="street-address"
                  className="w-full h-11 px-3 rounded-lg border border-slate-300 text-velcor-ink text-[13px] focus:border-velcor-green focus:outline-none focus:ring-2 focus:ring-velcor-green/20 placeholder:text-slate-400"
                  data-testid="chat-address"
                />
                <p className="text-slate-400 text-[10px] leading-tight pl-0.5">{t.address_hint}</p>
              </div>

              {error && (
                <div className="text-rose-600 text-[11.5px] font-bold leading-snug" data-testid="chat-error">{error}</div>
              )}

              <a
                href="/privacy"
                target="_blank"
                rel="noreferrer"
                className="text-velcor-green text-[11.5px] font-bold text-center underline underline-offset-2 hover:text-velcor-green-dark"
                data-testid="chat-privacy-link"
              >
                {t.privacy_policy}
              </a>

              <button
                type="submit"
                disabled={starting}
                className="w-full h-11 rounded-lg bg-velcor-green text-white font-extrabold text-[13.5px] hover:bg-velcor-green-dark disabled:opacity-60 inline-flex items-center justify-center gap-2"
                data-testid="chat-start-btn"
              >
                {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {t.start}
              </button>

              <p className="text-slate-400 text-[10px] leading-relaxed text-center">{t.powered}</p>
            </form>
          ) : (
            /* Conversation */
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-slate-50/40" data-testid="chat-messages">
                {messages.map((m) => {
                  // Specialized renderers for enrollment card kinds. They all
                  // share the same "agent bubble" framing (left-aligned with
                  // sender label) but render rich interactive content.
                  const isCardKind = ["plan_cards", "customer_data_card",
                                       "disclosure_card", "terms_card",
                                       "otp_card", "payment_card", "enrollment_complete"].includes(m.kind);
                  if (isCardKind) {
                    let CardInner = null;
                    if (m.kind === "plan_cards" && Array.isArray(m.plan_card_data)) {
                      CardInner = (
                        <div className="space-y-2">
                          {m.plan_card_data.map((p) => (
                            <PlanCardCustomer key={p.plan_id} plan={p} lang={lang}
                              sessionId={sessionId}
                              onSelected={() => loadTranscript(sessionId, true)} />
                          ))}
                        </div>
                      );
                    } else if (m.kind === "customer_data_card" && m.customer_data_card_data) {
                      CardInner = <CustomerDataCard data={m.customer_data_card_data} lang={lang}
                        sessionId={sessionId} onSubmitted={() => loadTranscript(sessionId, true)} />;
                    } else if (m.kind === "disclosure_card" && m.disclosure_data) {
                      CardInner = <DisclosureCardCustomer data={m.disclosure_data} lang={lang}
                        sessionId={sessionId} onAcked={() => loadTranscript(sessionId, true)} />;
                    } else if (m.kind === "terms_card" && m.terms_data) {
                      CardInner = <TermsCardCustomer data={m.terms_data} lang={lang}
                        sessionId={sessionId} onSigned={() => loadTranscript(sessionId, true)} />;
                    } else if (m.kind === "otp_card" && m.otp_data) {
                      CardInner = <OtpCardCustomer data={m.otp_data} lang={lang}
                        sessionId={sessionId} onVerified={() => loadTranscript(sessionId, true)} />;
                    } else if (m.kind === "payment_card" && m.payment_data) {
                      CardInner = <PaymentCardCustomer data={m.payment_data} lang={lang} />;
                    } else if (m.kind === "enrollment_complete" && m.complete_data) {
                      CardInner = <CompleteCardCustomer data={m.complete_data} lang={lang} />;
                    }
                    return (
                      <div key={m.id} className="flex justify-start">
                        <div className="max-w-[90%] w-full space-y-2">
                          {m.sender_role === "agent" && m.sender_name && (
                            <div className="px-1 mb-0.5 text-[10px] font-extrabold uppercase tracking-wider text-velcor-green inline-flex items-center gap-1">
                              <User className="w-2.5 h-2.5" strokeWidth={2.6} />
                              {m.sender_name} · Velcor Support
                            </div>
                          )}
                          {m.text && (
                            <div className="px-3.5 py-2 rounded-2xl rounded-bl-md bg-velcor-green/5 border border-velcor-green/30 text-velcor-ink text-[13px]">
                              {m.text}
                            </div>
                          )}
                          {CardInner}
                        </div>
                      </div>
                    );
                  }
                  return (
                  <div key={m.id}
                       className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[78%] ${m.role === "user" ? "" : "flex flex-col"}`}>
                      {m.sender_role === "agent" && m.sender_name && (
                        <div className="px-1 mb-0.5 text-[10px] font-extrabold uppercase tracking-wider text-velcor-green inline-flex items-center gap-1">
                          <User className="w-2.5 h-2.5" strokeWidth={2.6} />
                          {m.sender_name} · Velcor Support
                        </div>
                      )}
                      <div className={`px-3.5 py-2 rounded-2xl text-[13px] leading-relaxed whitespace-pre-wrap break-words ${
                        m.role === "user"
                          ? "bg-velcor-green text-white rounded-br-md"
                          : m.sender_role === "agent"
                          ? "bg-velcor-green/5 text-velcor-ink border border-velcor-green/30 rounded-bl-md"
                          : "bg-white text-velcor-ink border border-slate-200 rounded-bl-md"
                      }`}>
                        {m.text}
                      </div>
                    </div>
                  </div>
                  );
                })}

                {/* Quick-reply chips — only shown right after the greeting
                    (1 assistant message, no user reply yet). Once the user
                    sends anything they disappear permanently. */}
                {messages.length === 1 && messages[0]?.role === "assistant" && !escalated && !sending && (
                  <div className="flex flex-wrap gap-1.5 pt-1" data-testid="chat-quick-replies">
                    {(lang === "en" ? QUICK_REPLIES_EN : QUICK_REPLIES_ES).map((q) => (
                      <button
                        key={q.id}
                        type="button"
                        onClick={() => sendQuickReply(q.text)}
                        disabled={sending}
                        className="px-3 py-1.5 rounded-full bg-white border border-velcor-green/40 text-velcor-green text-[12px] font-extrabold hover:bg-velcor-green hover:text-white hover:border-velcor-green disabled:opacity-50 transition-colors"
                        data-testid={`quick-reply-${q.id}`}
                      >
                        {q.label}
                      </button>
                    ))}
                  </div>
                )}

                {sending && (
                  <div className="flex justify-start" data-testid="chat-typing">
                    <div className="flex flex-col items-start gap-1">
                      <div className="px-1 text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                        {t.typing}
                      </div>
                      <div className="px-3.5 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-400 text-[12.5px] inline-flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "120ms" }} />
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "240ms" }} />
                      </div>
                    </div>
                  </div>
                )}
                {/* Agent typing bubble — drives "Carlos escribiendo…" when
                    the human agent assigned to the chat is actively typing.
                    We hide the AI's own typing bubble while in agent flow
                    (the `sending` indicator only fires for outbound POSTs). */}
                {agentTyping && !sending && (
                  <div className="flex justify-start" data-testid="chat-agent-typing">
                    <div className="flex flex-col items-start gap-1">
                      <div className="px-1 text-[10px] font-extrabold uppercase tracking-wider text-velcor-green inline-flex items-center gap-1">
                        <User className="w-2.5 h-2.5" strokeWidth={2.6} />
                        {agentName ? `${agentName} ${lang === "en" ? "is typing…" : "está escribiendo…"}` : t.typing}
                      </div>
                      <div className="px-3.5 py-2.5 rounded-2xl bg-velcor-green/5 border border-velcor-green/30 text-slate-400 text-[12.5px] inline-flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 bg-velcor-green rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-1.5 h-1.5 bg-velcor-green rounded-full animate-bounce" style={{ animationDelay: "120ms" }} />
                        <span className="w-1.5 h-1.5 bg-velcor-green rounded-full animate-bounce" style={{ animationDelay: "240ms" }} />
                      </div>
                    </div>
                  </div>
                )}
                {escalated && !chatClosed && !agentJoined && (
                  <div className="flex justify-center" data-testid="chat-escalated">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 font-bold text-[11.5px]">
                      <User className="w-3.5 h-3.5" />
                      {t.escalated}
                    </div>
                  </div>
                )}
                {chatClosed && (
                  <div className="flex justify-center" data-testid="chat-closed">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600 font-bold text-[11.5px]">
                      {lang === "en" ? "Chat closed" : "Chat finalizado"}
                    </div>
                  </div>
                )}
              </div>

              <form onSubmit={sendMessage} className="border-t border-slate-100 px-3 py-2.5 flex items-center gap-2 bg-white">
                <input
                  type="text" value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    // Throttled typing heartbeat — fire at most once every
                    // 2s while the customer is actively typing. The backend
                    // extends `customer_typing_until` by 6s per heartbeat.
                    if (!sessionId || !agentJoined) return;
                    const now = Date.now();
                    if (now - (typingHbRef.current || 0) > 2000) {
                      typingHbRef.current = now;
                      axios.post(`${API}/chat/${sessionId}/typing`).catch(() => {});
                    }
                  }}
                  placeholder={chatClosed ? (lang === "en" ? "This chat is closed" : "Este chat está finalizado") : t.placeholder}
                  disabled={chatClosed}
                  className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-velcor-ink text-[13px] focus:border-velcor-green focus:outline-none disabled:bg-slate-50 disabled:cursor-not-allowed"
                  data-testid="chat-input"
                />
                <button type="submit" disabled={sending || !input.trim() || chatClosed}
                        className="w-10 h-10 rounded-lg bg-velcor-green text-white grid place-items-center disabled:opacity-50 hover:bg-velcor-green-dark"
                        data-testid="chat-send">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>

              <div className="px-3 pb-2 text-slate-400 text-[10px] text-center">
                {t.powered} ·{" "}
                <button onClick={reset} className="underline hover:text-slate-600" data-testid="chat-reset">
                  {lang === "en" ? "Start over" : "Empezar de nuevo"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

